import { BackgroundImage, ContainerElement } from '@typedream/components';
import {
  ContainerElement as ContainerElementType,
  DreamValue,
} from '@typedream/core';
import {
  findNodePath,
  isElementEmpty,
  PlateRenderElementProps,
} from '@udecode/plate-core';
import { pick } from 'lodash';
import Image from 'next/legacy/image';
import styled from 'styled-components';
import { Edit } from '@styled-icons/boxicons-regular';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { useDropBlock } from '../../../plugins/dnd';
import { insertItemIntoContainer } from '../transforms/insertItemIntoContainer';
import { ContainerElementPluginOptions } from '../types';
import { ELEMENT_CONTAINER } from '../../../pluginKeys';
import { DreamEditor } from '../../../types';

interface DropTargetProps {
  element: ContainerElementType
}

const StyledSyncButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border-radius: 10px;
  background-color: var(--background-button);
  border: 1px solid var(--border-modal);
  box-shadow: var(--shadow-modal);
  transition: all 0.1s;
  color: var(--accent) !important;
  cursor: pointer;
  margin: auto;
`;

const StyledSyncActionsContainer = styled.div`
  padding: 10px;
  margin: auto;
  text-align: center;
`;

function EmptySyncedContainerActions() {
  const editor = useTPlateEditorRef();

  return (
    <StyledSyncActionsContainer
      contentEditable={false}
    >
      <StyledSyncButton
        role="none"
        onClick={() => {
          // handle opening the suggestion
          editor.emitter.emit('toolbar:toggle-synced-container-selector');
        }}
      >
        Select existing Synced Containers
      </StyledSyncButton>
      <div
        style={{
          margin: '10px 0px',
          color: 'var(--text-lighter)',
        }}
      >
        or
      </div>
      <StyledSyncButton
        role="none"
        onClick={() => {
          // handle opening the suggestion
          editor.emitter.emit('toolbar:create-synced-container');
        }}
      >
        Create new Synced Container
      </StyledSyncButton>
    </StyledSyncActionsContainer>
  );
}

function DropTarget(props: DropTargetProps) {
  const editor = useTPlateEditorRef();
  const { element } = props;

  const [{ canDrop, isOver }, dropTarget] = useDropBlock(element, {
    drop: (item) => {
      const path = findNodePath(editor, element);
      if (path) {
        insertItemIntoContainer(editor, path, item);
      }
    },
  });

  return (
    <div
      ref={dropTarget}
      contentEditable={false}
      style={{
        flex: 1,
        width: '100%',
        color: 'var(--accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        padding: 40,
        borderRadius: 10,
        outline: isOver
          ? '3px solid var(--dropline)'
          : '1px dashed var(--accent)',
        background: isOver && canDrop ? 'var(--dropBackground)' : undefined,
      }}
    >
      <span>Drop items inside</span>
    </div>
  );
}

const StyledSyncedContainerOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  inset: 2px;
  transition: all .5s;
  opacity: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    opacity: 1;
  }
`;

export function ContainerElementEditor(
  props: PlateRenderElementProps<DreamValue, ContainerElementType>,
) {
  const { editor, element, children } = props;

  const { options } = getTPlugin<ContainerElementPluginOptions>(
    editor as unknown as DreamEditor,
    ELEMENT_CONTAINER,
  );

  const isSyncedContainer = options?.isTransclusionEnabled
    && !!element.transclusion_enabled;
  const isEmpty = isElementEmpty(editor, element);
  const isNewSyncedContainer = isSyncedContainer && isEmpty && !element.transclusion_reference_id;

  return (
    <ContainerElement
      {...pick(props, ['element', 'className', 'attributes'])}
      backgroundImageElement={
        element.backgroundImage ? (
          <BackgroundImage
            src={element.backgroundImage}
            darken={element.backgroundDarken}
            optimizedImageComponent={Image}
          />
        ) : undefined
      }
    >
      {isEmpty && !isNewSyncedContainer && <DropTarget element={element} />}
      {isNewSyncedContainer && <EmptySyncedContainerActions />}
      {isEmpty ? (
        <div
          style={{
            height: 0,
            color: 'transparent',
            outline: 'none',
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
      {isSyncedContainer && element.transclusion_reference_id && (
        <StyledSyncedContainerOverlay contentEditable={false}>
          <StyledSyncButton onClick={() => {
            if (!element.transclusion_reference_id) return;
            options.onEditSyncedReference?.(element?.transclusion_reference_id || '');
          }}
          >
            <Edit size={25} style={{ marginRight: '0.5rem' }} />
            Edit Synced Container
          </StyledSyncButton>
        </StyledSyncedContainerOverlay>
      )}
    </ContainerElement>
  );
}
